import {fromFileReference} from './fileReference';

type TaskComments = {edges?: ({node?: {comment?: string | null}} | null)[]} | null;
type TaskLike = {description?: string | null; comments?: TaskComments};

export type ReferenceSites = {description: Set<string>; comments: Set<string>};

/**
 * The DraftJS entity types that carry an inline file reference.
 *
 * Exported rather than inlined because four separate rules depend on getting this set
 * right — the refcount, the deferred-delete pending set, the Files-list visibility
 * rule, and copy-on-insert — and a second copy elsewhere would be free to drift from
 * this one. A kind missing here makes a file referenced only by that kind read as
 * unreferenced, so the Files list offers a delete action and re-opens the
 * dangling-reference hole the inline-image design closed by construction.
 */
export const MEDIA_ENTITY_TYPES: ReadonlySet<string> = new Set(['IMAGE', 'FILE', 'VIDEO', 'AUDIO']);

/**
 * Extracts the file ids of every inline media reference — image, file chip, video or
 * audio — in one stored rich-text value.
 */
export const collectReferencedFileIds = (content: string | null | undefined): Set<string> => {
	const ids = new Set<string>();
	if (!content) return ids;

	let parsed: {entityMap?: Record<string, {type?: string; data?: {src?: string}} | null>};
	try {
		parsed = JSON.parse(content);
	} catch {
		// Legacy or partially-migrated content may not be valid JSON. It cannot
		// contain one of our references if it cannot be parsed, so report none
		// rather than letting a parse error escape into a render path.
		return ids;
	}

	Object.values(parsed?.entityMap ?? {}).forEach(entity => {
		// The type is checked for being a string before it is upper-cased: this runs in the
		// Files-list render path, and a non-string `type` in a hand-edited or imported blob
		// would otherwise throw out of the forEach and take the whole task modal down —
		// which is exactly what the catch above exists to prevent for malformed content.
		if (typeof entity?.type !== 'string' || !MEDIA_ENTITY_TYPES.has(entity.type.toUpperCase())) return;
		const fileId = fromFileReference(entity?.data?.src ?? '');
		if (fileId) ids.add(fileId);
	});

	return ids;
};

/**
 * Where each file is referenced across a task.
 *
 * Description and comments are kept apart because the Files list treats them
 * differently: a description reference is listed, a comment-only one is not.
 */
export const referenceSitesForTask = (task: TaskLike): ReferenceSites => ({
	description: collectReferencedFileIds(task?.description),
	comments: new Set((task?.comments?.edges ?? []).flatMap(edge => [...collectReferencedFileIds(edge?.node?.comment)])),
});

/** File ids present in the old content and absent from the new one. */
export const removedReferences = (before: string | null | undefined, after: string | null | undefined): string[] => {
	const stillThere = collectReferencedFileIds(after);
	return [...collectReferencedFileIds(before)].filter(id => !stillThere.has(id));
};

/**
 * Whether a file appears in the task's Files list, and whether it may be deleted there.
 *
 * A file referenced by the description is listed like any attachment but cannot be
 * deleted from the list — the text owns it, so removing it from the text is the only
 * way to remove it, which is what makes broken references impossible. A file
 * referenced only by a comment is not listed at all, and neither is one referenced
 * by both the description and a comment.
 *
 * The answer depends only on WHERE the file is referenced, never on which kind of
 * reference it is: a chip, a player and an image in the same surface all resolve
 * identically. A per-kind rule here would be a way to get one kind wrong on its own.
 */
export const fileVisibility = (fileId: string, sites: ReferenceSites): {visible: boolean; canDelete: boolean} => {
	const inDescription = sites.description.has(fileId);
	const inComments = sites.comments.has(fileId);

	// Not referenced anywhere: an ordinary attachment, untouched.
	if (!inDescription && !inComments) return {visible: true, canDelete: true};

	// The text owns it. It is listed only when the description is its only home.
	return {visible: inDescription && !inComments, canDelete: false};
};
